import { keepPreviousData, useQuery } from '@tanstack/react-query';
import useGlynt from '../useGlynt';
import { VendorsApiResponse } from '../../../types/vendor-types';

interface UseVendorSearchParams {
  /** Server-side search term matched against vendor aliases. */
  search?: string;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}

/**
 * Server-side vendor search. Hits `/vendors?alias_contains=` so callers no
 * longer need to download the whole catalog to filter locally. Vendor names are
 * covered by the API's auto-created name aliases. Debounce the `search` value
 * before passing it in (see useDebouncedValue).
 */
export default function useVendorSearch({
  search,
  limit = 20,
  offset = 0,
  enabled,
}: UseVendorSearchParams = {}) {
  const glynt = useGlynt();
  const trimmed = search?.trim() || '';

  return useQuery<VendorsApiResponse>({
    queryKey: ['vendors', 'alias_contains', trimmed, limit, offset],
    queryFn: async () =>
      (await glynt).vendor.getVendors({
        advanced: true,
        limit,
        offset,
        aliasContains: trimmed || undefined,
      }),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    ...(enabled !== undefined && { enabled }),
  });
}
